// src/views/Tarjetas/EESShabilitadas.jsx
import React, { useEffect, useMemo, useState } from "react";
import { FaTrashAlt } from "react-icons/fa";
import useEscClose from "../../useEscClose";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";
import { shouldRunOnce } from "../../../utils/devOnce";
import EliminarHabi from "./EliminarHabi";
import FiltroEESS from "./FiltroEESS";

/* ================== Vista principal ================== */
export default function EESShabilitadas({ FiltroComponent = null }) {
  // datos
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [totalBackend, setTotalBackend] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  // filtros (guardamos id y número para rehidratar; consultamos por id)
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  useEscClose(() => {
    setMostrarFiltros(false);
    document.activeElement?.blur();
  }, mostrarFiltros);

  // { tarjetaId?, tarjetaNumero?, eessId?, eessNombre? }
  const [filtros, setFiltros] = useState({});

  const [rowToDelete, setRowToDelete] = useState(null);
  useEscClose(() => {
    setRowToDelete(false);
    document.activeElement?.blur();
  }, rowToDelete);

  // paginación
  const [paginaActual, setPaginaActual] = useState(1); // 1-based
  const [porPagina, setPorPagina] = useState(50);

  const API_LIST = apiRoutes?.eessHabilitadas?.list || null;

  const hasActiveFilters = useMemo(() => {
    const f = filtros || {};
    return !!(
      (f.tarjetaId && String(f.tarjetaId).trim()) ||
      (f.tarjetaNumero && String(f.tarjetaNumero).trim()) ||
      (f.eessId && String(f.eessId).trim())
    );
  }, [filtros]);

  const buildParams = (opt = {}) => {
    const p = new URLSearchParams();
    if (opt.page !== undefined) p.append("page", opt.page); // 0-based para backend
    if (opt.size !== undefined) p.append("size", opt.size);

    // enviamos SOLO tarjetaId si hay
    if (opt.tarjetaId) {
      p.append("tarjetaId", opt.tarjetaId);
    }

    // ✅ enviar el ID de estación (EESS_SQ_COD)
    if (opt.eessId) {
      p.append("eessId", opt.eessId);
    }

    return p;
  };

  const fetchList = async (
    page = paginaActual,
    size = porPagina,
    f = filtros,
  ) => {
    setCargando(true);
    setErrorMsg("");
    try {
      if (!API_LIST) throw new Error("API no configurada");
      const params = buildParams({ page: page - 1, size, ...f });
      const json = await appFetchJson(`${API_LIST}?${params.toString()}`);
      const arr = Array.isArray(json?.items) ? json.items : [];
      setItems(arr);
      setTotalBackend(Number(json?.totalElements ?? arr.length));
    } catch (e) {
      console.error("Error al obtener EE.SS. habilitadas:", e);
      setItems([]);
      setTotalBackend(0);
      setErrorMsg("No se pudo consultar.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (!shouldRunOnce("EESShabilitadas:init")) return;
    fetchList(1, porPagina, filtros);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cambiarPagina = (n) => {
    const totalPag = Math.max(1, Math.ceil(totalBackend / porPagina));
    const next = Math.min(Math.max(1, n), totalPag);
    setPaginaActual(next);
    fetchList(next, porPagina, filtros);
  };

  const manejarCambioCantidad = (e) => {
    const nueva = Number(e.target.value);
    setPorPagina(nueva);
    setPaginaActual(1);
    fetchList(1, nueva, filtros);
  };

  // se llama desde el filtro inyectado
  const aplicarFiltros = (vals) => {
    // Tarjeta
    const tarjetaId =
      Array.isArray(vals.tarjetaIds) && vals.tarjetaIds.length
        ? String(vals.tarjetaIds[0])
        : vals.tarjetaId
          ? String(vals.tarjetaId)
          : "";

    const tarjetaNumero =
      Array.isArray(vals.tarjetaNumero) && vals.tarjetaNumero.length
        ? String(vals.tarjetaNumero[0])
        : vals.tarjeta
          ? String(vals.tarjeta)
          : "";

    // EE.SS. (tomamos el id de estación = EESS_SQ_COD)
    const eessId = vals.eessId ? String(vals.eessId) : "";
    const eessNombre = vals.eessNombre ? String(vals.eessNombre) : "";

    // Guardamos ambos para rehidratar; fetch usa tarjetaId y eessId
    const f = {
      tarjetaId,
      tarjetaNumero,
      eessId, // ← se envía al backend
      eessNombre, // ← rehidratación/visual
    };

    setFiltros(f);
    setPaginaActual(1);
    fetchList(1, porPagina, f);
  };

  const limpiarSeleccion = () => {
    setFiltros({});
    setPaginaActual(1);
    fetchList(1, porPagina, {});
  };

  // 🔄 refrescar después de eliminar
  const handleDeleted = () => {
    const nextTotal = Math.max(0, totalBackend - 1);
    const totalPaginasNext = Math.max(1, Math.ceil(nextTotal / porPagina));
    const nextPage = Math.min(paginaActual, totalPaginasNext);
    setPaginaActual(nextPage);
    setRowToDelete(null); // cerrar modal
    fetchList(nextPage, porPagina, filtros);
  };

  // paginación cálculos
  const totalPaginas = Math.max(1, Math.ceil(totalBackend / porPagina));
  const startIndex = (paginaActual - 1) * porPagina;
  const endIndex = Math.min(startIndex + porPagina, totalBackend);
  const indiceInicio = totalBackend === 0 ? 0 : startIndex + 1;
  const indiceFin = endIndex;

  return (
    <div className="page">
      <h1 className="page-title">⛽ EE.SS. habilitadas</h1>

      <div className="toolbar">
        <div className="inline-flex items-center gap-2">
          <button
            className="btn-fil"
            onClick={() => setMostrarFiltros((v) => !v)}
          >
            {mostrarFiltros ? "Ocultar Filtros" : "Ver Filtros"}
          </button>

          {hasActiveFilters && (
            <button className="btn-fil ml-2" onClick={limpiarSeleccion}>
              Limpiar Selección
            </button>
          )}
        </div>
        <div className="flex items-center">{/* acciones derechas */}</div>
      </div>

      {/* Filtro inyectable (opcional) */}
      {mostrarFiltros && FiltroComponent && (
        <FiltroComponent
          initialFilters={{
            tarjetaId: filtros.tarjetaId || null,
            tarjeta: filtros.tarjetaNumero || "",
            eessId: filtros.eessId || "",
            eessNombre: filtros.eessNombre || "",
          }}
          onSubmit={(vals) => {
            aplicarFiltros(vals);
            setMostrarFiltros(false);
          }}
          onClose={() => setMostrarFiltros(false)}
        />
      )}

      <div className="control-block">
        <label htmlFor="pp" className="label-strong">
          Mostrar por página:
        </label>
        <select
          id="pp"
          className="select"
          value={porPagina}
          onChange={manejarCambioCantidad}
        >
          <option value={30}>30</option>
          <option value={40}>40</option>
          <option value={50}>50</option>
        </select>
      </div>

      {cargando ? (
        <div className="loading-text">Cargando…</div>
      ) : errorMsg ? (
        <div className="error-text">{errorMsg}</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="table-card">
              <thead className="table-head">
                <tr>
                  <th className="cell text-left">Tarjeta</th>
                  <th className="cell text-left">EE.SS.</th>
                  <th className="cell-center">Acción</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td className="cell text-center" colSpan={3}>
                      Ningún ítem encontrado.
                    </td>
                  </tr>
                ) : (
                  items.map((row, idx) => (
                    <tr
                      key={row.id ?? idx}
                      className={idx % 2 === 0 ? "row-alt" : "bg-white"}
                    >
                      <td className="cell">{row.tarjetaNumero}</td>
                      <td className="cell">
                        {row.eessNombre}
                        {row.eessDireccion ? (
                          <>
                            <br />
                            <span className="text-gray-600">
                              {row.eessDireccion}
                            </span>
                          </>
                        ) : null}
                      </td>
                      <td className="cell-center space-x-2">
                        <button
                          className="icon-btn"
                          title="Eliminar"
                          onClick={() => setRowToDelete(row)}
                        >
                          <FaTrashAlt />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            {`${totalBackend.toLocaleString("es-ES")} item${
              totalBackend !== 1 ? "s" : ""
            } encontrados, mostrando de ${indiceInicio} a ${indiceFin}.`}
          </div>

          <nav className="pager">
            <button
              onClick={() => cambiarPagina(1)}
              disabled={paginaActual === 1}
              className={paginaActual === 1 ? "pager-off" : "pager-btn"}
            >
              Primera
            </button>
            <button
              onClick={() => cambiarPagina(paginaActual - 1)}
              disabled={paginaActual === 1}
              className={paginaActual === 1 ? "pager-off" : "pager-btn"}
            >
              Anterior
            </button>

            {[...Array(totalPaginas)].map((_, i) => {
              const num = i + 1;
              if (
                num === 1 ||
                num === totalPaginas ||
                (num >= paginaActual - 2 && num <= paginaActual + 2)
              ) {
                return (
                  <button
                    key={num}
                    onClick={() => cambiarPagina(num)}
                    className={
                      paginaActual === num ? "pager-active" : "pager-btn"
                    }
                  >
                    {num}
                  </button>
                );
              }
              if (num === paginaActual - 3 || num === paginaActual + 3) {
                return (
                  <span key={num} className="px-2 py-1">
                    …
                  </span>
                );
              }
              return null;
            })}

            <button
              onClick={() => cambiarPagina(paginaActual + 1)}
              disabled={paginaActual === totalPaginas}
              className={
                paginaActual === totalPaginas ? "pager-off" : "pager-btn"
              }
            >
              Próxima
            </button>
            <button
              onClick={() => cambiarPagina(totalPaginas)}
              disabled={paginaActual === totalPaginas}
              className={
                paginaActual === totalPaginas ? "pager-off" : "pager-btn"
              }
            >
              Última
            </button>
          </nav>
        </>
      )}

      {mostrarFiltros && (
        <FiltroEESS
          initialFilters={{
            tarjetaId: filtros.tarjetaId || null,
            tarjeta: filtros.tarjetaNumero || "",
            eessId: filtros.eessId || "",
            eessNombre: filtros.eessNombre || "",
          }}
          onSubmit={aplicarFiltros}
          onClose={() => setMostrarFiltros(false)}
        />
      )}

      {/* Modal eliminar */}
      {rowToDelete && (
        <div className="modal-backdrop" onClick={() => setRowToDelete(null)}>
          <div
            className="modal-panel-lg no-x"
            onClick={(e) => e.stopPropagation()}
          >
            <EliminarHabi
              registro={rowToDelete}
              onClose={() => setRowToDelete(null)}
              onDeleted={handleDeleted}
            />
          </div>
        </div>
      )}
    </div>
  );
}
